



import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Credentials, amplifyService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/amplify';
import { Auth } from '@kasasa/kloans-common-ui/dist/consumer-app/services/session/types';
import { AxiosResponse } from 'axios';
import { getConsumerStatus, setJwt } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication';
import { GetConsumerStatusResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/ConsumerStatus';
import { getCallbackUrl } from '@kasasa/kloans-common-ui/dist/utils/callback';
import { requiredOnlyValidation } from '@kasasa/kloans-common-ui/dist/validations';
import { ProductInviteType } from '@/enums/ProductInviteType';
import { RawLocation } from 'vue-router';

@Component({
	validations: {
		credentials: {
			username: requiredOnlyValidation.validations,
			password: requiredOnlyValidation.validations,
		},
	},
})
export default class Login extends Vue {
	@Prop({ default: null })
	profileConnectionToken!: string | null;

	loginText = 'Log In';

	credentials: Credentials = { username: '', password: '' };

	validateBtn = true;

	get usernameErrors() {
		const errors: Array<string> = [];
		if (!this.$v.credentials.username?.$dirty) {
			return errors;
		}
		if (!this.$v.credentials.username?.required) {
			errors.push('Username is required.');
		}

		return errors;
	}

	get passwordErrors() {
		const errors: Array<string> = [];
		if (!this.$v.credentials.password?.$dirty) {
			return errors;
		}
		if (!this.$v.credentials.password?.required) {
			errors.push('Password is required.');
		}

		return errors;
	}

	public beforeCreate() {
		this.$store.commit('setHeadline', 'Log in with your Kasasa account.');
	}

	public validate() {
		this.$v.credentials.$touch();
		if (!this.$v.credentials.$invalid) {
			this.validateBtn = false;
			this.loginText = 'Logging In...';
			this.credentials.username = this.credentials.username.toLowerCase();
			this.signIn();
		}
	}

	public async signIn() {
		await amplifyService.signIn(this.credentials, {
			onSuccess: this.loginSuccess,
			onFailure: this.loginFailure,
			mfaRequired: this.mfaRedirect,
		});
	}

	public async loginSuccess() {
		const jwt: string = (localStorage.getItem(Auth.ID_TOKEN) as string);
		setJwt(jwt);

		if (this.profileConnectionToken) {
			await this.redirectToProductInviteViewForProfileConnection();
			return;
		}

		try {
			const response: AxiosResponse<GetConsumerStatusResponse> = await getConsumerStatus();
			const consumerStatus: string = response.data.data.status;

			if (consumerStatus === 'confirmed') {
				this.$router.push({ name: 'updateSecurityQuestions' });
				return;
			}

			await this.$store.dispatch('clearError');

			window.location.href = getCallbackUrl();
		} catch (error) {
			this.loginFailure('A consumer record cannot be found for this user. Contact Kasasa.');
		}
	}

	private async redirectToProductInviteViewForProfileConnection(): Promise<void> {
		await this.$router.push({
			name: 'kasasaLoanInviteVerification',
			params: {
				token: this.profileConnectionToken ?? '',
			},
			query: {
				type: ProductInviteType.CONNECT_EXISTING_PROFILE.toString(),
			},
		});
	}

	public loginFailure(message: string) {
		this.$store.dispatch('showError', message);
		this.loginText = 'Log In';
		this.validateBtn = true;
	}

	public mfaRedirect(): void {
		const mfaRoute: RawLocation = {
			name: 'mfa',
		};

		if (this.profileConnectionToken) {
			mfaRoute.query = {
				token: this.profileConnectionToken,
			};
		}

		this.$router.push(mfaRoute);
	}
}
